import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { PACKAGES, PACKAGES_INTRO, PACKAGE_CONFIG, formatEUR } from "@/data/content";
import { useT, useLanguage } from "@/i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export const Packages = () => {
  const t = useT();
  const { lang } = useLanguage();
  const [availMap, setAvailMap] = useState({});

  useEffect(() => {
    let active = true;
    const units = [...new Set(PACKAGES.filter((p) => p.unit).map((p) => p.unit))];
    Promise.all(
      units.map((u) =>
        fetch(`${API}/api/availability?unit=${u}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => [u, d])
          .catch(() => [u, null]),
      ),
    ).then((entries) => {
      if (!active) return;
      const map = {};
      entries.forEach(([u, d]) => { if (d) map[u] = d; });
      setAvailMap(map);
    });
    return () => { active = false; };
  }, []);

  const renderPrice = (p) => {
    if (p.priceEur) {
      return (
        <div className="flex items-baseline gap-2">
          <span className="text-base font-medium text-[#62646B]">{t(p.priceFromLabel)}</span>
          <span className="font-heading text-4xl sm:text-5xl font-semibold text-[#0A0A0F]">
            {PACKAGE_CONFIG.currencySymbol}{formatEUR(p.priceEur)}
          </span>
        </div>
      );
    }
    return (
      <span className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0F]">
        {t(p.priceFromLabel)}
      </span>
    );
  };

  return (
    <section id="paketi" data-testid="packages-section" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="uppercase text-xs tracking-[0.25em] text-[#1A1A1F] font-bold">
            {t(PACKAGES_INTRO.eyebrow)}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-4">
            {t(PACKAGES_INTRO.title)}
          </h2>
          <p className="text-[#62646B] leading-relaxed mt-6">{t(PACKAGES_INTRO.description)}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {PACKAGES.map((p, i) => {
            const av = p.unit ? availMap[p.unit] : null;
            const unitSoldOut = av?.sold_out === true;
            return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`package-card-${i}`}
              className={`relative p-8 lg:p-10 rounded-3xl border transition-all duration-300 ${
                p.available
                  ? "bg-white border-[#0A0A0F]/20 wf-glow hover:-translate-y-1"
                  : "bg-[#F4F2EC]/50 border-[#E8E7E2]"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold ${
                    p.available
                      ? "bg-[#0A0A0F] text-white"
                      : "border border-[#E8E7E2] text-[#62646B]"
                  }`}
                >
                  {t(p.badge)}
                </span>
                <div className="flex flex-wrap items-center gap-2 text-xs text-[#62646B]">
                  {p.available && av && (
                    unitSoldOut ? (
                      <span
                        data-testid={`package-availability-${i}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FBEDED] text-[#7A1F1F] border border-[#E7B7B7]"
                      >
                        {lang === "sr" ? "Rasprodato" : "Fully reserved"}
                      </span>
                    ) : (
                      <span
                        data-testid={`package-availability-${i}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#F1F7F1] text-[#2F6B33] border border-[#CDE3CD]"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#2F6B33]" />
                        {lang === "sr"
                          ? `Još ${av.remaining} od ${av.capacity}`
                          : `${av.remaining} of ${av.capacity} left`}
                      </span>
                    )
                  )}
                  {p.meta.map((m, idx) => (
                    <span key={idx} className="px-2 py-1 rounded-full border border-[#E8E7E2]">
                      {t(m)}
                    </span>
                  ))}
                </div>
              </div>

              <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-6">
                {t(p.name)}
              </h3>

              <div className="mt-6 pb-6 border-b border-[#E8E7E2]">
                {renderPrice(p)}
              </div>

              <ul className="mt-6 space-y-3">
                {p.features.map((f, idx) => (
                  <li key={idx} className="flex gap-3 text-sm text-[#62646B] leading-relaxed">
                    <Check size={16} className="text-[#0A0A0F] shrink-0 mt-0.5" />
                    {t(f)}
                  </li>
                ))}
              </ul>

              {p.available ? (
                unitSoldOut ? (
                  <span
                    data-testid={`package-cta-${i}`}
                    className="mt-8 w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full border border-[#E8E7E2] text-[#62646B] font-semibold cursor-not-allowed"
                  >
                    {lang === "sr" ? "Rasprodato" : "Fully reserved"}
                  </span>
                ) : (
                  <a
                    href={p.unit ? `/reserve?unit=${p.unit}` : "/reserve"}
                    data-testid={`package-cta-${i}`}
                    className="mt-8 w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors duration-300"
                  >
                    {t(p.cta)}
                  </a>
                )
              ) : (
                <span
                  data-testid={`package-cta-${i}`}
                  className="mt-8 w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full border border-[#E8E7E2] text-[#62646B] font-semibold cursor-not-allowed"
                >
                  {t(p.cta)}
                </span>
              )}
            </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
